import React, { Component } from "react";
import { t } from "../../../components/CustomComponent.js";
import ICON from "../icons.js";
import { isRent, isSale } from "../property_util.js";

/* ============================================================================
 * PropertyLead — กล่อง "ลงทะเบียนสนใจทรัพย์" ของหน้ารายละเอียด (ฝั่งลูกค้า)
 *
 * ข้อมูลที่กรอกลงตาราง customers ชุดเดียวกับปุ่ม "เพิ่มลูกค้า" ของแอดมิน จึงวางช่อง
 * ให้ตรงกับฟอร์มนั้นทีละบล็อก (ข้อมูลติดต่อ → ความต้องการ) แต่เหลือเฉพาะช่องที่
 * ลูกค้าตอบเองได้จริง
 *
 * ช่องที่ตัดออกเพราะเป็นงานภายในของทีมขาย หลังบ้านเติมให้เองตอนบันทึก :
 *   ช่องทางที่เข้ามา (= website) · ระดับความสนใจ · สถานะการติดตาม · ผู้ดูแล
 *   วันติดตามถัดไป · งบประมาณ · ทำเล · ประเภททรัพย์ · รายการทรัพย์ที่สนใจ
 * (สามอย่างหลังอ่านจากประกาศที่กำลังเปิดดูอยู่ ไม่ต้องให้ลูกค้าพิมพ์ซ้ำ)
 *
 * ตัวคอมโพเนนต์ไม่ยิง API เอง — ตรวจค่าที่กรอกแล้วส่ง payload ให้ property.js
 * ผ่าน onSubmit ซึ่งคืน true เมื่อบันทึกสำเร็จ (กล่องจะล้างฟอร์มแล้วปิดตัวเอง)
 * ========================================================================== */

/* ช่วงเวลาที่สะดวก — ค่าต้องตรงกับ VISIT_TIME_SLOTS ฝั่ง backend (home.constant.js)
   "ยังไม่ระบุ" คือตัวเลือกค่าว่าง ไม่ได้เป็นรหัสหนึ่งในชุดนี้ */
const VISIT_SLOTS = ["morning", "afternoon", "evening"];

const EMPTY_FORM = {
  name: "",
  phone: "",
  email: "",
  line_id: "",
  deal: "",
  visit_at: "",
  visit_time: "",
  note: "",
  consent: false,
};

/* วันนี้ในรูป YYYY-MM-DD — ใช้เป็นค่า min ของช่องวันที่ ไม่ให้เลือกวันที่ผ่านมาแล้ว
   <input type="date"> รับค่าเป็นรูปแบบนี้เท่านั้น และเป็นรูปแบบเดียวกับที่ API ต้องการ */
const todayISO = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

export default class PropertyLead extends Component {
  state = { form: { ...EMPTY_FORM }, sending: false };

  componentDidMount() {
    window.addEventListener("keydown", this.onKey);
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.open && this.props.open) {
      document.body.classList.add("hm-pd-noscroll"); // กันหน้าเว็บด้านหลังเลื่อนตาม

      /* ไม่ล้างค่าที่กรอกค้างไว้ตอนเปิดใหม่ — ส่งไม่สำเร็จ (เน็ตหลุด) แล้วเปิดมาใหม่
         ต้องไม่บังคับให้พิมพ์ทั้งฟอร์มซ้ำ (ส่งสำเร็จแล้วฟอร์มถูกล้างใน Save อยู่แล้ว)
         มีแต่ประเภทดีลที่ต้องตรวจใหม่ทุกครั้ง เพราะกดข้ามไปทรัพย์ใบอื่นได้ */
      const deals = this.deals();
      if (!deals.includes(this.state.form.deal)) this.setForm({ deal: deals[0] || "" });
    }
    if (prevProps.open && !this.props.open) document.body.classList.remove("hm-pd-noscroll");
  }

  componentWillUnmount() {
    window.removeEventListener("keydown", this.onKey);
    document.body.classList.remove("hm-pd-noscroll");
  }

  onKey = (event) => {
    if (this.props.open && event.key === "Escape" && !this.state.sending) this.props.onClose();
  };

  setForm = (patch) => this.setState((state) => ({ form: { ...state.form, ...patch } }));

  /* ประกาศใบนี้เปิดให้เช่าหรือขาย — ลูกค้าเลือกได้เฉพาะที่ประกาศมีจริง */
  deals = () => {
    const { data = {} } = this.props;
    return [isRent(data.deals) && "rent", isSale(data.deals) && "sale"].filter(Boolean);
  };

  Save = async () => {
    const { form, sending } = this.state;
    if (sending) return;

    const name = form.name.trim();
    const phone = form.phone.replace(/[^0-9]/g, "");
    const email = form.email.trim();

    // ตรวจให้ครบก่อนยิง API — ผู้ใช้ฝั่งลูกค้าไม่ควรเจอข้อความ error จากหลังบ้าน
    if (!name) return this.props.onWarn(t("lead_require_name"));
    if (phone.length < 9) return this.props.onWarn(t("lead_require_phone"));
    if (email && !/^\S+@\S+\.\S+$/.test(email)) return this.props.onWarn(t("lead_invalid_email"));
    if (!form.consent) return this.props.onWarn(t("lead_require_consent"));

    this.setState({ sending: true });
    const done = await this.props.onSubmit({
      name,
      phone,
      email,
      line_id: form.line_id.trim(),
      deal: form.deal || undefined,
      visit_at: form.visit_at || null,
      visit_time: form.visit_time || undefined,
      note: form.note.trim(),
    });
    this.setState({ sending: false });

    if (done) this.setState({ form: { ...EMPTY_FORM } });
  };

  render() {
    const { open, data = {}, agent = {}, onClose } = this.props;
    const { form, sending } = this.state;

    if (!open) return null;

    const deals = this.deals();

    return (
      <div className="hm-pd-modal" role="dialog" aria-modal="true" aria-label={t("lead_title")}>
        {/* คลิกพื้นหลังเพื่อปิด — ระหว่างส่งข้อมูลอยู่ห้ามปิด กันกดหลุดแล้วไม่รู้ผล */}
        <button
          type="button"
          className="hm-pd-modal__scrim"
          aria-label={t("cancel")}
          onClick={() => !sending && onClose()}
        />

        <div className="hm-pd-modal__box">
          <div className="hm-pd-modal__head">
            <div>
              <div className="hm-pd-modal__title">{t("lead_title")}</div>
              <div className="hm-pd-modal__desc">{t("lead_desc")}</div>
            </div>
            <button type="button" className="hm-pd-modal__close" onClick={onClose} disabled={sending}>
              <span className="hm-ico">{ICON.close}</span>
            </button>
          </div>

          <div className="hm-pd-modal__body">
            {/* ทรัพย์ที่กำลังดูอยู่ — ผูกให้อัตโนมัติ ลูกค้าไม่ต้องเลือกเอง */}
            <div className="hm-pd-lead__asset">
              <span className="hm-ico">{ICON.home}</span>
              <div className="hm-pd-lead__asset-text">
                <span className="hm-pd-lead__asset-name">{data.title || data.project}</span>
                <span className="hm-pd-lead__asset-meta">
                  {[data.code, data.zone].filter(Boolean).join(" · ")}
                </span>
              </div>
            </div>

            <div className="hm-pd-lead__section">{t("lead_section_contact")}</div>

            <div className="hm-pd-lead__grid">
              <label className="hm-pd-field">
                <span className="hm-pd-field__label">
                  {t("lead_name")} <i>*</i>
                </span>
                <input
                  type="text"
                  className="hm-pd-input"
                  placeholder={t("lead_name_placeholder")}
                  value={form.name}
                  onChange={(e) => this.setForm({ name: e.target.value })}
                />
              </label>

              <label className="hm-pd-field">
                <span className="hm-pd-field__label">
                  {t("lead_phone")} <i>*</i>
                </span>
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  className="hm-pd-input"
                  placeholder={t("lead_phone_placeholder")}
                  value={form.phone}
                  // รับเฉพาะตัวเลข — หลังบ้านเทียบเบอร์ซ้ำด้วยตัวเลขล้วน
                  onChange={(e) => this.setForm({ phone: e.target.value.replace(/[^0-9]/g, "") })}
                />
              </label>

              <label className="hm-pd-field">
                <span className="hm-pd-field__label">
                  {t("lead_email")} <em>({t("lead_optional")})</em>
                </span>
                <input
                  type="email"
                  className="hm-pd-input"
                  placeholder={t("lead_email_placeholder")}
                  value={form.email}
                  onChange={(e) => this.setForm({ email: e.target.value })}
                />
              </label>

              <label className="hm-pd-field">
                <span className="hm-pd-field__label">
                  {t("lead_line")} <em>({t("lead_optional")})</em>
                </span>
                <input
                  type="text"
                  className="hm-pd-input"
                  placeholder={t("lead_line_placeholder")}
                  value={form.line_id}
                  onChange={(e) => this.setForm({ line_id: e.target.value })}
                />
              </label>
            </div>

            <div className="hm-pd-lead__section">{t("lead_section_visit")}</div>

            <div className="hm-pd-lead__grid">
              {/* ประกาศที่ให้ทั้งเช่าและขาย ต้องรู้ว่าลูกค้าสนใจแบบไหน */}
              {deals.length > 1 && (
                <div className="hm-pd-field">
                  <span className="hm-pd-field__label">{t("lead_deal")}</span>
                  <div className="hm-pd-seg">
                    {deals.map((deal) => (
                      <button
                        key={deal}
                        type="button"
                        aria-pressed={form.deal === deal}
                        className={`hm-pd-seg__btn ${form.deal === deal ? "is-active" : ""}`}
                        onClick={() => this.setForm({ deal })}
                      >
                        {t(deal === "rent" ? "for_rent" : "for_sale")}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <label className="hm-pd-field">
                <span className="hm-pd-field__label">
                  {t("lead_visit_date")} <em>({t("lead_optional")})</em>
                </span>
                <input
                  type="date"
                  className="hm-pd-input"
                  min={todayISO()}
                  value={form.visit_at}
                  onChange={(e) => this.setForm({ visit_at: e.target.value })}
                />
              </label>

              <label className="hm-pd-field">
                <span className="hm-pd-field__label">
                  {t("lead_visit_time")} <em>({t("lead_optional")})</em>
                </span>
                <select
                  className="hm-pd-input"
                  value={form.visit_time}
                  onChange={(e) => this.setForm({ visit_time: e.target.value })}
                >
                  <option value="">{t("lead_visit_time_any")}</option>
                  {VISIT_SLOTS.map((slot) => (
                    <option key={slot} value={slot}>
                      {t(`visit_slot_${slot}`)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="hm-pd-field hm-pd-field--full">
                <span className="hm-pd-field__label">
                  {t("lead_note")} <em>({t("lead_optional")})</em>
                </span>
                <textarea
                  className="hm-pd-input hm-pd-textarea"
                  rows={3}
                  placeholder={t("lead_note_placeholder")}
                  value={form.note}
                  onChange={(e) => this.setForm({ note: e.target.value })}
                />
              </label>
            </div>

            {/* ความยินยอมให้เก็บข้อมูลส่วนบุคคล — ต้องติ๊กเองก่อนส่งเสมอ */}
            <label className="hm-pd-lead__consent">
              <input
                type="checkbox"
                checked={form.consent}
                onChange={(e) => this.setForm({ consent: e.target.checked })}
              />
              <span>
                {t("lead_consent")}{" "}
                <a href="/policy-privacy" target="_blank" rel="noreferrer">
                  {t("lead_consent_link")}
                </a>
              </span>
            </label>
          </div>

          <div className="hm-pd-modal__foot">
            {/* ผู้ดูแลที่จะเป็นคนติดต่อกลับ — บอกไว้ให้ลูกค้ารู้ว่าจะมีสายจากใคร */}
            {!!agent.nickname && (
              <span className="hm-pd-lead__agent">
                <span className="hm-ico">{ICON.headset}</span>
                {t("lead_contact_by")} {agent.nickname}
              </span>
            )}
            <div className="hm-pd-modal__actions">
              <button type="button" className="hm-pd-btn hm-pd-btn--outline" onClick={onClose} disabled={sending}>
                {t("cancel")}
              </button>
              <button type="button" className="hm-pd-btn hm-pd-btn--primary" onClick={this.Save} disabled={sending}>
                <span className="hm-ico">{ICON.check}</span>
                {sending ? t("lead_sending") : t("lead_submit")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
